/**
 * hashFile — compute the MD5 of a File/Blob in the browser without
 * ever loading the whole thing into memory as a single ArrayBuffer.
 *
 * Uses spark-md5's incremental API + FileReader in 4 MB slices. Yields
 * to the event loop between slices via `await new Promise(setTimeout,0)`
 * so the UI stays responsive on multi-GB videos.
 *
 * Usage:
 *   const md5 = await hashFile(file, ({sent, total, pct}) => …);
 *
 * Also exports `hashBlob(blob)` for computing per-chunk MD5s inline
 * during upload — that variant is a single-shot (no progress) since
 * chunks are ≤ 4 MB.
 */
import SparkMD5 from "spark-md5";

const HASH_SLICE = 4 * 1024 * 1024; // 4 MB — matches the upload chunk size

// Small helper — spark-md5's ArrayBuffer.hash gives you the MD5 in one
// shot but we want incremental so a 500 MB file doesn't hold 500 MB in
// memory at once. Both browser and Node paths supported.
function readBlobAsArrayBuffer(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error || new Error("FileReader failed"));
    fr.readAsArrayBuffer(blob);
  });
}

export async function hashFile(file, onProgress) {
  if (!file) throw new Error("hashFile: no file");
  const spark = new SparkMD5.ArrayBuffer();
  const total = file.size;
  let done = 0;
  for (let offset = 0; offset < total; offset += HASH_SLICE) {
    const end = Math.min(offset + HASH_SLICE, total);
    // eslint-disable-next-line no-await-in-loop
    const buf = await readBlobAsArrayBuffer(file.slice(offset, end));
    spark.append(buf);
    done = end;
    if (onProgress) {
      onProgress({
        sent: done,
        total,
        pct: Math.round((done / total) * 100),
      });
    }
    // Yield to the event loop so the UI paints — hashing a 500 MB file
    // in one microtask block freezes the page for 4-6 seconds.
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 0));
  }
  return spark.end(); // hex digest
}

export async function hashBlob(blob) {
  const buf = await readBlobAsArrayBuffer(blob);
  return SparkMD5.ArrayBuffer.hash(buf);
}
